import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import permissionStore from "./../api";
import loadingStore from "core/utils/loading";
import { Permissions } from "../model";
import PermissionsRow from "./user-permission-row";
import Header from "components/Header";
import { hasPermission, Permission } from "core/utils/roles";
import { useAuth } from "app/authHooks";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TablePlaceholder: React.FC<{ children: ReactNode }> = ({ children }) => (
  <TableRow>
    <TableCell colSpan={9} align="center">
      {children}
    </TableCell>
  </TableRow>
);

function PermissionsTable() {
  const { attributes } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [data, setData] = useState<Permissions[]>([]);
  const pageSize = 10;
  const { getPermissions } = permissionStore;
  const { loading } = loadingStore;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onInit(searchText, page);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, page]);

  const onInit = async (text: string, pageNum: number) => {
    try {
      const result = await getPermissions({
        searchText: text,
        pageIndex: pageNum - 1,
        pageSize,
      });
      setData(result?.data || []);
      setTotalPages(Math.ceil((result?.total || 0) / pageSize));
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  return (
    <Box sx={{ paddingBottom: "64px" }}>
      <Box display="flex" justifyContent="space-between">
        <Header title="Permissions" subtitle="Manage permissions" />
        {hasPermission("create:permissions" as Permission) && (
          <Box
            display="flex"
            flexDirection="row"
            gap="10px"
            alignItems="center"
          >
            <Button
              type="submit"
              variant="contained"
              onClick={() => navigate("/security/permissions/add-edit")}
            >
              Add Permission
            </Button>
          </Box>
        )}
      </Box>

      <Box py="20px">
        <Accordion
          expanded={isExpanded}
          onChange={() => setIsExpanded(!isExpanded)}
        >
          <AccordionSummary
            sx={{ "&.Mui-expanded": { minHeight: "32px" } }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography>Filters</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Box
              display="flex"
              gap="16px"
              flexDirection="column"
              alignItems="flex-start"
            >
              <TextField
                fullWidth
                label="Enter name, description or entity"
                variant="outlined"
                sx={{ flex: 1 }}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
              />

              <Box display="flex" alignItems="center" gap="8px" width="100%">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSearchText("");
                    setPage(1);
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {[
                { field: "name", label: "Name" },
                { field: "description", label: "Description" },
                { field: "entity", label: "Entity" },
                { field: "created_at", label: "Created At" },
              ].map(({ field, label }) => (
                <TableCell key={field}>
                  <Typography noWrap variant="h5">
                    {label}{" "}
                  </Typography>
                </TableCell>
              ))}
              <TableCell>
                <Typography noWrap variant="h5">
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TablePlaceholder>
                <CircularProgress />
              </TablePlaceholder>
            )}
            {!loading && !data?.length && (
              <TablePlaceholder>
                <Typography variant="h5">No Data</Typography>
              </TablePlaceholder>
            )}
            {!loading &&
              data?.map((row, index) => (
                <PermissionsRow row={row} key={index} attributes={attributes} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          padding: "10px",
          boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Pagination
          className="my-3"
          count={totalPages}
          page={page}
          variant="outlined"
          onChange={(event, value) => setPage(value)}
        />
      </Box>
    </Box>
  );
}

export default observer(PermissionsTable);
